
import {
    defineComponent,
    PropType,
    computed,
    toRefs,
    ref,
    reactive,
    watch
} from 'vue';

import {
    ElDialog,
    ElForm,
    ElFormItem,
    ElInput,
    ElButton,
    vLoading,
    ElMessage
} from 'element-plus';

import EnumEditor from './Fields/Enum.vue';
import { ArtistId, ArtistModel } from '@/typings';
import { doCombineArtist, doCombineDiscogsArtist, doEditArtist, getMetaEditArtist } from '@/services';
import { online_links_default } from '@/basics/enums';
import { useRouter } from 'vue-router';
import EditorUpload from '@/components/Editor/Fields/EditorUpload.vue';

export default defineComponent({
    directives: {
        vLoading
    },
    components: {
        ElDialog,
        ElForm,
        ElFormItem,
        ElInput,
        ElButton,
        EnumEditor,
        EditorUpload
    },
    props: {
        visible: {
            type: Boolean,
            required: true
        },
        isKeepUrl: {
            type: Boolean,
            default: false
        },
        id: {
            type: String as unknown as PropType<ArtistId>,
            required: true
        }
    },
    emits: {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        'update:visible' (val:boolean) {
            return true;
        },
        change () {
            return true;
        }
    },
    setup (props, { emit }) {
        const {
            visible,
            id,
            isKeepUrl
        } = toRefs(props);

        const dialogFormVisible = computed({
            get: () => visible.value,
            set: (val) => emit('update:visible', val)
        });

        const dialogTitle = ref('');
        const everEdited = ref(false);
        const isCombine = ref(0);
        const handleCombine = (val:number) => {
            isCombine.value = val;
        };

        const combineId = ref('');
        const combineIdFrom = ref({
            index: 'discogs',
            name: ''
        });
        const newImage = ref('');

        const form_edited = () => {
            everEdited.value = true;
        };

        const loadings = reactive({
            formLoading: false
        });

        const formEditArtist = reactive<ArtistModel>({
            styles: [],
            name_roman: '',
            name_cn_simp: '',
            relate_styles: [],
            links: [],
            online_links: {},
            primary_img: '',
            type: {
                index: 0,
                name: '未分配'
            }
        } as unknown as ArtistModel);

        const initFormData = (artistInfo:ArtistModel) => {
            Object.assign(formEditArtist, artistInfo);

            dialogTitle.value = artistInfo.name;

            formEditArtist.online_links = JSON.parse(JSON.stringify(online_links_default));

            // TODO type
            for (const k in artistInfo.online_links) {
                (formEditArtist as any).online_links[k].url = (artistInfo as any).online_links[k].url;
            }
        };

        const refreshData = () => {
            loadings.formLoading = true;
            getMetaEditArtist(id.value).then((json) => {
                initFormData(json.data.info);
            }).finally(() => {
                loadings.formLoading = false;
            });
        };

        watch(() => {
            return visible.value;
        }, refreshData, {
            immediate: false,
        });

        const removeLinkIndex = (index:number) => {
            formEditArtist.links.splice(index, 1);
            form_edited();
        };
        const removeImgIndex = (index:number) => {
            formEditArtist.images.splice(index, 1);
            form_edited();
        };

        const choose_thumbnail = (index:number) => {
            formEditArtist.primary_img = formEditArtist.images[index].resource_url;
        };
        const router = useRouter();

        const handleUploadNewImage = () => {
            formEditArtist.images.push({ resource_url: newImage.value, type: 'secondary', uri: '' });
            newImage.value = '';
        };

        const doConfirmEdit = () => {
            if (!everEdited.value && !isCombine.value) {
                ElMessage.warning('数据无变化');
                return;
            }
            loadings.formLoading = true;

            if (isCombine.value === 1) {
                doCombineArtist(id.value, { combineId: combineId.value }).then(() => {
                    ElMessage.success('感谢您的编辑');
                    dialogFormVisible.value = false;
                    router.replace({
                        path: '/meta/artist/' + combineId.value
                    });
                }).catch((json) => {
                    ElMessage.error(json.data.msg);
                }).finally(() => {
                    loadings.formLoading = false;
                });
            } else if (isCombine.value === 2) {
                doCombineDiscogsArtist(id.value, { combineIdFrom: combineIdFrom.value.index, combineId: combineId.value }).then((json) => {
                    Object.assign(formEditArtist, json.data.info);

                    ElMessage.success('感谢您的编辑');
                    dialogFormVisible.value = false;
                    if (isKeepUrl.value) {
                        router.push({
                            path: '/meta/artist/' + id.value
                        });
                    }
                }).catch((json) => {
                    ElMessage.error(json.data.msg);
                }).finally(() => {
                    loadings.formLoading = false;
                });
            } else {
                doEditArtist(id.value, formEditArtist).then((json) => {
                    Object.assign(formEditArtist, json.data.info);

                    ElMessage.success('感谢您的编辑');
                    dialogFormVisible.value = false;
                    emit('change');
                    if (isKeepUrl.value) {
                        router.push({
                            path: '/meta/artist/' + id.value
                        });
                    }
                }).catch((json) => {
                    ElMessage.error(json.data.msg);
                }).finally(() => {
                    loadings.formLoading = false;
                });
            }
        };

        return {
            dialogFormVisible,
            dialogTitle,
            isCombine,
            handleCombine,
            combineId,
            combineIdFrom,
            newImage,
            form_edited,
            loadings,
            formEditArtist,
            removeLinkIndex,
            removeImgIndex,
            choose_thumbnail,
            doConfirmEdit,
            handleUploadNewImage
        };
    }
});
